import {
  type BookScope,
  BookScopePicker,
  EMPTY_BOOK_SCOPE,
} from "@/components/lessonGenerator/BookScopePicker";
import { ReferenceSlideThumbnail } from "@/components/lessonGenerator/ReferenceSlideThumbnail";
import { SectionEditorModal } from "@/components/lessonGenerator/SectionEditorModal";
import { describeSection } from "@/components/lessonGenerator/countRules";
import type { GeneratorSection } from "@/components/lessonGenerator/types";
import { readsNothing } from "@/components/lessonGenerator/types";
import {
  type SavedSection,
  toSavedFields,
  toSectionPresetInput,
  useSectionPresets,
} from "@/lib/hooks/sectionPresets/useSectionPresets";
import { useTags } from "@/lib/hooks/tags/useTags";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { Button, LoadingSpinner } from "@ai-teach/ui-web";
import { useState } from "react";
import toast from "react-hot-toast";
import { HiOutlineDuplicate, HiOutlinePencil, HiOutlineTrash, HiPlus } from "react-icons/hi";

const CARD_WIDTH = 192;
const THUMBNAIL_WIDTH = CARD_WIDTH - 16;

/** The saved section as the editor takes it; its own id would link it to itself as a template. */
function toEditable(saved: SavedSection): GeneratorSection {
  return {
    id: saved.id,
    name: saved.name,
    template: saved.template,
    referenceSlide: saved.referenceSlide,
    context: saved.context,
    ...(saved.sectionScopes?.length ? { sectionScopes: saved.sectionScopes } : {}),
    ...(saved.count ? { count: saved.count } : {}),
    ...(saved.parent ? { parent: saved.parent } : {}),
    prompt: saved.prompt,
    randomizeChoicePositions: saved.randomizeChoicePositions,
    generateSentence: saved.generateSentence,
    slideName: getTemplateLabel(saved.template),
    numberSlides: false,
  };
}

function TemplateCard({
  section,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  section: SavedSection;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { tags } = useTags();
  const parentTag = section.parent
    ? (tags.find((one) => one.id === section.parent?.tagId)?.name ?? section.parent.tagId)
    : "";

  return (
    <li
      style={{ width: CARD_WIDTH }}
      className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-background p-2 transition-colors hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700"
    >
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${section.name}`}
        className="flex flex-col gap-2 text-left"
      >
        <ReferenceSlideThumbnail slide={section.referenceSlide.slide} width={THUMBNAIL_WIDTH} />

        <span className="block min-w-0 space-y-1">
          <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {section.name}
          </span>
          <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">
            {getTemplateLabel(section.template)}
          </span>
          <span className="line-clamp-2 text-[11px] text-gray-500 dark:text-gray-400">
            {describeSection(section, parentTag)}
          </span>
          <span className="block truncate font-mono text-[11px] text-gray-400">
            ref {section.referenceSlide.name}
          </span>
          {section.prompt !== "" && (
            <span className="line-clamp-3 text-[11px] leading-relaxed text-gray-600 dark:text-gray-300">
              {section.prompt}
            </span>
          )}
          {readsNothing(section) && (
            <span className="block text-[11px] text-warning-500">
              Nothing checked — written from the example slide alone.
            </span>
          )}
        </span>
      </button>

      <div className="mt-auto flex justify-end gap-0.5 border-t border-gray-200 pt-1.5 dark:border-gray-800">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${section.name}`}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-200"
        >
          <HiOutlinePencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          aria-label={`Duplicate ${section.name}`}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-200"
        >
          <HiOutlineDuplicate className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${section.name}`}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500"
        >
          <HiOutlineTrash className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

/**
 * Every saved section, previewed by the slide it is modelled on. A preset's section can follow one
 * of these, so editing it here reaches every preset that does.
 */
export function SectionTemplatePanel() {
  const { sections, loading, saveSection, updateSection, deleteSection } = useSectionPresets();
  const [editing, setEditing] = useState<SavedSection | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // A template is written for no one book, so one is named here only to list the parts to scope to.
  const [previewScope, setPreviewScope] = useState<BookScope>(EMPTY_BOOK_SCOPE);

  // A template is named, so an unnamed section falls back to what its template is called.
  const store = async (section: GeneratorSection) => {
    const name = section.name?.trim() || getTemplateLabel(section.template);
    const input = toSectionPresetInput(name, toSavedFields(section));
    try {
      if (editing) {
        await updateSection(editing.id, input);
        toast.success(`“${name}” updated`);
      } else {
        await saveSection(input);
        toast.success(`“${name}” saved`);
      }
    } catch {
      toast.error("Could not save the section template");
    }
  };

  const duplicate = async (section: SavedSection) => {
    const name = `${section.name} copy`;
    try {
      await saveSection(toSectionPresetInput(name, toSavedFields(section)));
      toast.success(`“${name}” created`);
    } catch {
      toast.error("Could not duplicate the section template");
    }
  };

  const remove = async (section: SavedSection) => {
    if (
      !window.confirm(
        `Delete “${section.name}”? Presets that follow it keep working from their own copy.`
      )
    ) {
      return;
    }
    try {
      await deleteSection(section.id);
      toast.success(`“${section.name}” deleted`);
    } catch {
      toast.error("Could not delete the section template");
    }
  };

  const openEditor = (section: SavedSection | null) => {
    setEditing(section);
    setIsEditorOpen(true);
  };

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-2">
          <p className="max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            A template holds a slide template, reference slide, book content and count, ready to
            drop into any preset or run. Name a book to list the parts a template can scope itself
            to.
          </p>
          <div className="grid max-w-xl grid-cols-3 gap-3">
            <BookScopePicker
              idPrefix="template-scope"
              value={previewScope}
              onChange={setPreviewScope}
            />
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<HiPlus className="h-4 w-4" />}
          onClick={() => openEditor(null)}
        >
          New template
        </Button>
      </div>

      {loading && sections.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : sections.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No section templates yet. Saved sections whose reference slide was deleted are left out.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-4">
          {sections.map((section) => (
            <TemplateCard
              key={section.id}
              section={section}
              onEdit={() => openEditor(section)}
              onDuplicate={() => void duplicate(section)}
              onDelete={() => void remove(section)}
            />
          ))}
        </ul>
      )}

      {isEditorOpen && (
        <SectionEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={store}
          allowSubslides
          hideSavedSections
          targetBook={null}
          bookScope={previewScope}
          {...(editing ? { section: toEditable(editing) } : {})}
        />
      )}
    </div>
  );
}
